import { useState } from "react";
import {
  Alert,
  Card,
  ErrorState,
  FormStack,
  Switch,
  Text,
} from "@marekl/adminer/components";
import { useConfirm, useT } from "@marekl/adminer/hooks";
import { apiErrorMessage, notifySuccess } from "@marekl/adminer/api";
import { pluralizeCzech } from "@marekl/adminer/format";
import {
  useMailSettings,
  useSaveMailSettings,
  type MailSettings,
  type MailSwitch,
} from "../../../lib/queries/mailOutbox";
import {
  MAIL_SECTION_LABELS,
  mailSwitchHint,
  mailSwitchLabel,
  reminderRulesSentence,
} from "./mailLabels";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

function disabledKinds(s: MailSettings): string[] {
  return s.sections.flatMap((sec) =>
    sec.switches.filter((sw) => !sw.enabled).flatMap((sw) => sw.kinds),
  );
}

function stoppedSentence(n: number): string {
  if (n === 0) return "E-mail je vypnutý.";
  const mails = pluralizeCzech(n, [
    "čekající e-mail",
    "čekající e-maily",
    "čekajících e-mailů",
  ]);
  const verb = pluralizeCzech(n, ["Neodejde", "Neodejdou", "Neodejde"]);
  return `E-mail je vypnutý. ${verb} ${n} ${mails}.`;
}

/**
 * The switches of /admin/maily: which kinds of mail cmfis sends
 * (internal/mail/settings.go). Each toggle saves at once. A switch-off asks
 * first, because it also stops the mails of that kind already waiting, and
 * switching back on does not send them.
 */
export function MailSettingsTab() {
  const { data: me } = useMe();
  // Čtení všeho sees which mails go out and switches none of them.
  const readOnly = readsOnlyAsGlobalViewer(me);
  const { data, isLoading, error, refetch } = useMailSettings();
  const save = useSaveMailSettings();
  const confirm = useConfirm();
  const t = useT();
  // A switch-on that failed. A switch-off shows its refusal in the dialog.
  const [failure, setFailure] = useState<unknown>(null);

  if (error) return <ErrorState error={error} onRetry={refetch} />;

  function toggle(sw: MailSwitch, on: boolean) {
    if (!data) return;
    const others = disabledKinds(data).filter((k) => !sw.kinds.includes(k));
    const label = mailSwitchLabel(sw.kinds);
    setFailure(null);
    if (on) {
      save.mutate(others, {
        onSuccess: () => notifySuccess(`E-mail „${label}“ je zapnutý.`),
        onError: setFailure,
      });
      return;
    }
    confirm({
      title: "Vypnout e-mail",
      description: (
        <>
          E-mail <strong>{label}</strong> přestane odcházet. Neodejdou ani ty,
          které už čekají na odeslání, a po opětovném zapnutí se nedošlou. V
          přehledu e-mailů zůstanou se stavem „Vypnuto“.
        </>
      ),
      confirmLabel: "Vypnout",
      variant: "danger",
      onConfirm: async () => {
        const res = await save.mutateAsync([...others, ...sw.kinds]);
        notifySuccess(stoppedSentence(res.rows_disabled));
      },
    });
  }

  return (
    <>
      {/* The rules every reminder obeys, with the scheduler's own numbers. */}
      {data && (
        <Alert variant="info" title="Jak chodí upomínky">
          {reminderRulesSentence(data.reminders)}
        </Alert>
      )}
      {failure != null && (
        <Alert variant="err">{apiErrorMessage(failure, t)}</Alert>
      )}
      {isLoading && (
        <Card>
          <Card.Content isLoading>{null}</Card.Content>
        </Card>
      )}
      {data?.sections.map((sec) => (
        <Card key={sec.key}>
          <Card.Header title={MAIL_SECTION_LABELS[sec.key] ?? sec.key} />
          <Card.Content>
            <FormStack>
              {sec.switches.map((sw) => (
                <Switch
                  key={sw.kinds.join()}
                  label={mailSwitchLabel(sw.kinds)}
                  hint={mailSwitchHint(sw)}
                  checked={sw.enabled}
                  disabled={sw.locked || save.isPending || readOnly}
                  onChange={(e) => toggle(sw, e.target.checked)}
                />
              ))}
              {sec.key === "accounts" && (
                <Text variant="muted">
                  Tyto e-maily nesou odkaz na nastavení hesla a vypnout je
                  nejde: bez nich by se nikdo nedostal ke svému účtu.
                </Text>
              )}
            </FormStack>
          </Card.Content>
        </Card>
      ))}
    </>
  );
}
